import React, { useState, useEffect, useCallback, useContext, useRef } from "react";
import ReactDOM from "react-dom";
import { Search, X as ClearIcon } from "react-feather";
import { MENUITEMS } from "../SideBar-Layout/Menu";
import ListOfMenu from "./Bookmark/ListOfMenu";
import EmpltyClass from "./Bookmark/EmptyClass";
import CustomContext from "../../_helper/customizer";
import { getLocalItem, ROLE, ROLE_PERMISSION } from "../../Constant/common";
import { SuperAdmin } from "../../Constant";

const Searchbar = ({ autoFocus = false }) => {
  const role = getLocalItem("role");

  const permissions = React.useMemo(() => {
    try {
      const parsed = JSON.parse(getLocalItem("permissions"));
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [role]);

  const isModuleAllowed = useCallback(
    (parentModule, module) => {
      if (parentModule === "Support") {
        return permissions.some(
          (p) =>
            p?.parent_module_name?.trim()?.toLowerCase() === "support" ||
            p?.module_name?.trim()?.toLowerCase() === "support",
        );
      }
      return permissions.some(
        (p) => p?.parent_module_name === parentModule && p?.module_name === module,
      );
    },
    [permissions],
  );

  const finalMenu = React.useMemo(() => {
    const menuCopy = MENUITEMS.map((item) => ({
      ...item,
      Items: item.Items.map((sub) => {
        const copy = { ...sub };
        if (sub.children) {
          copy.children = sub.children.map((c) => ({ ...c }));
        }
        return copy;
      }),
    }));

    let result = menuCopy
      .map((menuItem) => {
        if (menuItem.menutitle !== ROLE) {
          menuItem.Items = menuItem.Items.map((item) => {
            if (item.children) {
              item.children = item.children.filter((child) =>
                isModuleAllowed(menuItem.menutitle, child.title),
              );
            }
            return item;
          }).filter((item) =>
            item.children
              ? item.children.length > 0
              : isModuleAllowed(menuItem.menutitle, item.title),
          );
        } else {
          menuItem.Items = menuItem.Items.filter((item) =>
            isModuleAllowed(item.title, ROLE_PERMISSION),
          );
        }
        return menuItem;
      })
      .filter((menuItem) => menuItem.Items && menuItem.Items.length > 0);

    // Organization is SuperAdmin-only — strip it from the sidebar for all other roles
    if (role !== SuperAdmin) {
      result = result
        .map((group) => {
          if (group.menutitle === "Master") {
            return {
              ...group,
              Items: group.Items.map((item) => {
                if (item.children) {
                  return {
                    ...item,
                    children: item.children.filter((c) => c.title !== "Organization"),
                  };
                }
                return item;
              }).filter((item) => !item.children || item.children.length > 0),
            };
          }
          return group;
        })
        .filter((group) => group.Items && group.Items.length > 0);
    }

    // Payroll and Overtime are restricted for SuperAdmin — strip them from the sidebar
    if (role === SuperAdmin) {
      result = result
        .map((group) => {
          if (group.menutitle === "HR") {
            return {
              ...group,
              Items: group.Items.map((item) => {
                if (item.children) {
                  return {
                    ...item,
                    children: item.children.filter(
                      (c) => c.title !== "Payroll" && c.title !== "Overtime",
                    ),
                  };
                }
                return item;
              }).filter((item) => !item.children || item.children.length > 0),
            };
          }
          return group;
        })
        .filter((group) => group.Items && group.Items.length > 0);
    }

    return result;
  }, [role, isModuleAllowed]);

  const [searchValue, setSearchValue] = useState("");
  const [searchIcon, setSearchIcon] = useState(false);
  const [searchToggle, setSearchToggle] = useState(false);
  const [searchResult, setSearchResult] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });
  const { setIsClose } = useContext(CustomContext);
  const inputRef = useRef(null);
  const containerRef = useRef(null);

  /* Force padding-left after mount — bypasses any !important CSS rule */
  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.style.setProperty("padding-left", "34px", "important");
      inputRef.current.style.setProperty(
        "padding-right",
        searchValue ? "34px" : "12px",
        "important",
      );
      if (autoFocus) {
        inputRef.current.focus();
      }
    }
  }, [autoFocus, searchValue]);

  /* Calculate dropdown position */
  useEffect(() => {
    if (searchToggle && containerRef.current) {
      const updatePosition = () => {
        const rect = containerRef.current.getBoundingClientRect();
        setDropdownPosition({
          top: rect.bottom + 8,
          left: rect.left,
          width: rect.width,
        });
      };
      updatePosition();
      window.addEventListener("resize", updatePosition);
      window.addEventListener("scroll", updatePosition, true);
      return () => {
        window.removeEventListener("resize", updatePosition);
        window.removeEventListener("scroll", updatePosition, true);
      };
    }
  }, [searchToggle]);

  const escFunction = useCallback(
    (event) => {
      if (event.keyCode === 27) {
        setSearchValue("");
        setSearchResult([]);
        setIsClose(false);
      }
    },
    [setIsClose],
  );

  useEffect(() => {
    document.addEventListener("keydown", escFunction, false);
    return () => document.removeEventListener("keydown", escFunction, false);
  }, [escFunction]);

  const checkSearchResultEmpty = (items) => setSearchIcon(!items.length);
  const addFix = () => setSearchToggle(true);
  const removeFix = () => {
    setSearchValue("");
    setSearchToggle(false);
  };

  const handleSearchKeyword = (keyword) => {
    keyword = keyword.toLowerCase();
    keyword ? addFix() : removeFix();
    const items = [];
    setSearchValue(keyword);
    finalMenu.forEach((menuItems) => {
      menuItems.Items.forEach((mItems) => {
        if (
          mItems.title.replace(/\s/g, "").toLowerCase().includes(keyword) &&
          mItems.type === "link"
        ) {
          items.push(mItems);
        }
        if (!mItems.children) return;
        mItems.children.forEach((subItems) => {
          if (
            subItems.title.replace(/\s/g, "").toLowerCase().includes(keyword) &&
            subItems.type === "link"
          ) {
            subItems.icon = mItems.icon;
            items.push(subItems);
          }
          if (!subItems.children) return;
          subItems.children.forEach((subSubItems) => {
            if (subSubItems.title.replace(/\s/g, "").toLowerCase().includes(keyword)) {
              subSubItems.icon = mItems.icon;
              items.push(subSubItems);
            }
          });
        });
      });
    });
    checkSearchResultEmpty(items);
    setSearchResult(items);
  };

  return (
    <div ref={containerRef} style={{ position: "relative", width: "100%" }}>
      {/* Icon pinned inside — pointer-events none so clicks pass through to input */}
      <span
        aria-hidden="true"
        style={{
          position: "absolute",
          left: 10,
          top: "50%",
          transform: "translateY(-50%)",
          display: "flex",
          alignItems: "center",
          pointerEvents: "none",
          color: "var(--text-muted)",
          zIndex: 2,
          lineHeight: 1,
        }}
      >
        <Search size={13} strokeWidth={2.5} />
      </span>

      <input
        ref={inputRef}
        type="text"
        placeholder="Search menu"
        value={searchValue}
        onChange={(e) => handleSearchKeyword(e.target.value)}
        style={{
          width: "100%",
          borderRadius: 999,
        }}
        onFocus={(e) => {
          e.target.style.setProperty("border-color", "var(--input-border-focus)", "important");
          e.target.style.setProperty("box-shadow", "0 0 0 3px rgba(99,102,241,0.1)", "important");
        }}
        onBlur={(e) => {
          e.target.style.setProperty("border-color", "var(--input-border)", "important");
          e.target.style.setProperty("box-shadow", "none", "important");
        }}
      />

      {/* Clear button — visible when search has a value */}
      {searchValue && (
        <button
          type="button"
          onClick={removeFix}
          aria-label="Clear search"
          style={{
            position: "absolute",
            right: 8,
            top: "50%",
            transform: "translateY(-50%)",
            background: "rgba(100,116,139,0.1)",
            border: "none",
            padding: 3,
            cursor: "pointer",
            color: "var(--text-muted)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            borderRadius: 4,
            lineHeight: 1,
            zIndex: 2,
          }}
        >
          <ClearIcon size={12} strokeWidth={2.5} />
        </button>
      )}

      {/* Render dropdown via portal to escape navbar overflow */}
      {searchToggle &&
        ReactDOM.createPortal(
          <div
            style={{
              position: "fixed",
              top: `${dropdownPosition.top}px`,
              left: `${dropdownPosition.left}px`,
              width: `${dropdownPosition.width}px`,
              zIndex: 99999,
            }}
          >
            <ListOfMenu
              searchValue={searchValue}
              searchResult={searchResult}
              removeFix={removeFix}
              searchBar={true}
              searchToggle={searchToggle}
            />
            <EmpltyClass searchIcon={searchIcon} search={true} />
          </div>,
          document.body,
        )}
    </div>
  );
};

export default Searchbar;
